import { ChevronIcon } from 'assets/icons';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { Spinner } from 'common/spinner';
import {
  DriveAccount,
  DriveAuthCancelled,
  DriveFile,
  DrivePopupBlocked,
  getDriveAccount,
} from 'helpers/google-drive';
import {
  DrivePickerClosed,
  pickDriveFiles,
  switchPickerAccount,
} from 'helpers/google-drive-file-picker';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from '../vault-import.module.scss';

interface Props {
  onFilesPicked: (files: DriveFile[]) => void;
  /** The user backed out of Google's sign-in, or left this screen. */
  onClose: () => void;
  onError?: (message: string) => void;
}

/**
 * Opens Google's own Drive picker straight away. When the browser blocks the
 * sign-in popup (no click behind it, e.g. a deep link), it waits for one.
 * Closing the picker comes back here, showing the connected account and
 * letting the user switch to another one.
 */
export const GoogleDriveFilePicker = ({
  onFilesPicked,
  onClose,
  onError,
}: Props) => {
  const [busy, setBusy] = useState(true);
  const [account, setAccount] = useState<DriveAccount | null>(null);
  const started = useRef(false);

  const refreshAccount = useCallback(async () => {
    setAccount(await getDriveAccount().catch(() => null));
  }, []);

  const open = useCallback(async () => {
    setBusy(true);
    try {
      const files = await pickDriveFiles();
      if (files.length === 0) {
        onError?.('None of the picked files can be imported.');
        await refreshAccount();
        setBusy(false);
        return;
      }
      onFilesPicked(files);
    } catch (err) {
      if (err instanceof DrivePopupBlocked) {
        setBusy(false);
        return;
      }
      if (err instanceof DrivePickerClosed) {
        await refreshAccount();
        setBusy(false);
        return;
      }
      if (err instanceof DriveAuthCancelled) {
        onClose();
        return;
      }
      onError?.(err instanceof Error ? err.message : 'Failed to connect');
      await refreshAccount();
      setBusy(false);
    }
  }, [onFilesPicked, onClose, onError, refreshAccount]);

  const switchAccount = async () => {
    setBusy(true);
    try {
      await switchPickerAccount();
    } catch (err) {
      if (!(err instanceof DriveAuthCancelled)) {
        onError?.(
          err instanceof Error ? err.message : 'Failed to switch accounts',
        );
      }
      setBusy(false);
      return;
    }
    await refreshAccount();
    open();
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    open();
  }, [open]);

  return (
    <>
      <div className={styles.headerBar}>
        <button
          type="button"
          aria-label="Back"
          className={styles.backBtn}
          onClick={onClose}
        >
          <ChevronIcon className={styles.backIcon} />
        </button>
        <h1 className={styles.headerTitle}>Google Drive</h1>
        {account && !busy ? (
          <button
            type="button"
            className={styles.headerPillBtn}
            onClick={switchAccount}
          >
            Switch account
          </button>
        ) : (
          <span className={styles.headerSpacer} aria-hidden />
        )}
      </div>
      <div className={styles.center}>
        {busy ? (
          <>
            <Spinner />
            <p className={styles.muted}>Opening Google Drive&hellip;</p>
          </>
        ) : (
          <>
            {account?.email && (
              <p className={styles.muted}>
                Connected to Google Drive as <strong>{account.email}</strong>
              </p>
            )}
            <p className={styles.muted}>
              Pick the photos, videos and documents to bring in. You can sort
              them into albums next.
            </p>
            <Button
              buttonStyle={ButtonStyle.Primary}
              buttonSize={ButtonSize.Large}
              onClick={open}
            >
              Choose from Google Drive
            </Button>
          </>
        )}
      </div>
    </>
  );
};
